import React, { useState, useRef, useEffect } from 'react';
import { Upload as UploadIcon, X, Film, Hash, AtSign, ChevronDown, Check, Loader, Send, Inbox } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function Upload({ onNavigate }) {
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAccounts, setShowAccounts] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState('');
  const [publishMode, setPublishMode] = useState('direct');
  const [scheduleMode, setScheduleMode] = useState('schedule');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const inputRef = useRef();

  useEffect(() => {
    supabase.from('tiktok_accounts')
      .select('id, username, display_name, avatar_url, followers, connected')
      .eq('connected', true)
      .then(({ data }) => {
        const list = data ?? [];
        setAccounts(list);
        if (list.length > 0) setSelectedAccount(list[0]);
      });
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && f.type.startsWith('video/')) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); setPreview(URL.createObjectURL(f)); }
  };

  const handleUpload = async () => {
    if (!file || !selectedAccount) return;
    setUploading(true);
    setError('');

    try {
      // 1. Dosyayı direkt Supabase Storage'a yükle
      const { data: { user } } = await supabase.auth.getUser();
      const ext = file.name.split('.').pop();
      const path = `${user.id}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('videos')
        .upload(path, file, { contentType: file.type, upsert: false });
      if (uploadError) throw new Error('Dosya yükleme başarısız: ' + uploadError.message);

      // 2. DB kaydını oluştur
      const scheduled_at = scheduleMode === 'schedule' && scheduleDate && scheduleTime
        ? new Date(`${scheduleDate}T${scheduleTime}`).toISOString()
        : null;

      const status = scheduled_at ? 'scheduled' : scheduleMode === 'now' ? 'publishing' : 'draft';

      const { data: videoData, error: dbError } = await supabase.from('videos').insert({
        user_id: user.id,
        account_id: selectedAccount.id,
        title: file.name,
        caption,
        hashtags: hashtags.split(/\s+/).filter(Boolean),
        storage_path: path,
        file_name: file.name,
        file_size: file.size,
        status,
        scheduled_at,
      }).select().single();
      if (dbError) throw new Error(dbError.message);

      // Hemen yayınla — queue'ye değil direkt TikTok'a gönder
      if (scheduleMode === 'now' && videoData?.id) {
        const { error: pubError } = await supabase.functions.invoke('videos-publish', { body: { video_id: videoData.id } });
        if (pubError) throw new Error('TikTok yayınlama hatası: ' + pubError.message);
      }

      setUploading(false);
      setUploaded(true);
      setTimeout(() => onNavigate(scheduleMode === 'now' ? 'dashboard' : 'queue'), 1500);
    } catch (e) {
      setUploading(false);
      setError(e.message);
    }
  };

  const getAvatarLetter = (acc) => {
    return (acc.username || acc.display_name || 'T')[0].toUpperCase();
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Upload Video</h1>
          <p style={styles.subtitle}>Select a video to schedule for TikTok</p>
        </div>
      </div>

      {error && (
        <div style={{ background: 'var(--pink-light)', color: 'var(--pink)', padding: '10px 16px', borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
          {error}
        </div>
      )}

      {accounts.length === 0 && (
        <div style={{ background: 'rgba(0,212,255,0.07)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 14, padding: '16px 20px', marginBottom: 20, fontSize: 13, color: 'var(--text-secondary)' }}>
          Henüz TikTok hesabı bağlanmamış. <button onClick={() => onNavigate('tiktok')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Hesap bağla</button>
        </div>
      )}

      {/* Publish mode selector */}
      <div style={styles.publishModeRow}>
        <button
          style={{
            ...styles.publishModeBtn,
            background: publishMode === 'direct' ? 'var(--bg-card)' : 'transparent',
            borderColor: publishMode === 'direct' ? 'var(--accent)' : 'var(--border)',
            boxShadow: publishMode === 'direct' ? 'var(--shadow-accent)' : 'none',
          }}
          onClick={() => setPublishMode('direct')}
        >
          <div style={{ ...styles.publishModeIcon, background: publishMode === 'direct' ? 'var(--accent-light)' : 'var(--bg-secondary)' }}>
            <Send size={18} color={publishMode === 'direct' ? 'var(--accent)' : 'var(--text-muted)'} />
          </div>
          <div style={styles.publishModeText}>
            <p style={{ ...styles.publishModeTitle, color: publishMode === 'direct' ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              Direct Post
            </p>
            <p style={styles.publishModeDesc}>Published directly to your TikTok profile</p>
          </div>
          <div style={{
            ...styles.publishModeRadio,
            borderColor: publishMode === 'direct' ? 'var(--accent)' : 'var(--border)',
            background: publishMode === 'direct' ? 'var(--accent)' : 'transparent',
          }}>
            {publishMode === 'direct' && <div style={styles.publishModeRadioDot} />}
          </div>
        </button>

        <button
          style={{
            ...styles.publishModeBtn,
            background: publishMode === 'inbox' ? 'var(--bg-card)' : 'transparent',
            borderColor: publishMode === 'inbox' ? 'var(--accent)' : 'var(--border)',
            boxShadow: publishMode === 'inbox' ? 'var(--shadow-accent)' : 'none',
          }}
          onClick={() => setPublishMode('inbox')}
        >
          <div style={{ ...styles.publishModeIcon, background: publishMode === 'inbox' ? 'var(--accent-light)' : 'var(--bg-secondary)' }}>
            <Inbox size={18} color={publishMode === 'inbox' ? 'var(--accent)' : 'var(--text-muted)'} />
          </div>
          <div style={styles.publishModeText}>
            <p style={{ ...styles.publishModeTitle, color: publishMode === 'inbox' ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              Send to Inbox
            </p>
            <p style={styles.publishModeDesc}>Saved as a draft in your TikTok inbox</p>
          </div>
          <div style={{
            ...styles.publishModeRadio,
            borderColor: publishMode === 'inbox' ? 'var(--accent)' : 'var(--border)',
            background: publishMode === 'inbox' ? 'var(--accent)' : 'transparent',
          }}>
            {publishMode === 'inbox' && <div style={styles.publishModeRadioDot} />}
          </div>
        </button>
      </div>

      <div style={styles.layout}>
        <div style={styles.leftCol}>
          {!file ? (
            <div
              style={{
                ...styles.dropzone,
                borderColor: dragOver ? 'var(--accent)' : 'var(--border)',
                background: dragOver ? 'var(--accent-light)' : 'var(--bg-card)',
              }}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            >
              <input ref={inputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handleFile} />
              <div style={styles.dropzoneIcon}>
                <UploadIcon size={32} color="var(--accent)" />
              </div>
              <p style={styles.dropzoneText}>Drag & drop your video here</p>
              <p style={styles.dropzoneSub}>or click to browse</p>
              <div style={styles.dropzoneTags}>
                <span style={styles.tag}>MP4</span>
                <span style={styles.tag}>MOV</span>
                <span style={styles.tag}>WebM</span>
                <span style={styles.tag}>Max 500MB</span>
              </div>
            </div>
          ) : (
            <div style={styles.previewCard}>
              <div style={styles.previewTop}>
                <video src={preview} style={styles.previewVideo} controls />
                <button style={styles.removeBtn} onClick={() => { setFile(null); setPreview(null); }}>
                  <X size={16} />
                </button>
              </div>
              <div style={styles.fileInfo}>
                <Film size={16} color="var(--accent)" />
                <div>
                  <p style={styles.fileName}>{file.name}</p>
                  <p style={styles.fileSize}>{(file.size / 1024 / 1024).toFixed(1)} MB</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={styles.rightCol}>
          <div style={styles.formGroup}>
            <label style={styles.label}><AtSign size={14} style={{ marginRight: 6 }} /> TikTok Account</label>
            <div style={styles.selectWrapper}>
              <button style={styles.selectBtn} onClick={() => setShowAccounts(!showAccounts)}>
                {selectedAccount ? (
                  <>
                    <div style={styles.accountAvatar}>
                      {selectedAccount.avatar_url
                        ? <img src={selectedAccount.avatar_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        : getAvatarLetter(selectedAccount)}
                    </div>
                    <span style={styles.selectBtnText}>@{selectedAccount.username}</span>
                  </>
                ) : (
                  <span style={{ ...styles.selectBtnText, color: 'var(--text-muted)' }}>Hesap seçin</span>
                )}
                <ChevronDown size={16} color="var(--text-secondary)" />
              </button>
              {showAccounts && (
                <div style={styles.dropdown}>
                  {accounts.map(acc => (
                    <button
                      key={acc.id}
                      style={styles.dropdownItem}
                      onClick={() => { setSelectedAccount(acc); setShowAccounts(false); }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={styles.accountAvatar}>
                        {acc.avatar_url
                          ? <img src={acc.avatar_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          : getAvatarLetter(acc)}
                      </div>
                      <div>
                        <p style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>@{acc.username}</p>
                        <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{(acc.followers || 0).toLocaleString()} followers</p>
                      </div>
                      {selectedAccount?.id === acc.id && <Check size={14} color="var(--accent)" style={{ marginLeft: 'auto' }} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Caption</label>
            <textarea
              style={styles.textarea}
              placeholder="Write something about your video..."
              value={caption}
              onChange={e => setCaption(e.target.value)}
              maxLength={2200}
              rows={4}
            />
            <p style={styles.charCount}>{caption.length}/2200</p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}><Hash size={14} style={{ marginRight: 6 }} /> Hashtags</label>
            <input
              style={styles.input}
              placeholder="#tiktok #trending #viral"
              value={hashtags}
              onChange={e => setHashtags(e.target.value)}
            />
            <p style={styles.hint}>Separate with spaces. Improves discoverability on TikTok.</p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Publish Time</label>
            <div style={styles.scheduleToggle}>
              {['now', 'schedule'].map(opt => (
                <button
                  key={opt}
                  style={{
                    ...styles.toggleBtn,
                    background: scheduleMode === opt ? 'var(--accent)' : 'transparent',
                    color: scheduleMode === opt ? '#fff' : 'var(--text-secondary)',
                  }}
                  onClick={() => setScheduleMode(opt)}
                >
                  {opt === 'now' ? 'Publish Now' : 'Schedule'}
                </button>
              ))}
            </div>
            {scheduleMode === 'schedule' && (
              <div style={styles.dateTimeRow}>
                <input type="date" style={styles.dateInput} value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} />
                <input type="time" style={styles.dateInput} value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} />
              </div>
            )}
          </div>

          <button
            style={{ ...styles.submitBtn, opacity: (file && selectedAccount) ? 1 : 0.5, cursor: (file && selectedAccount) ? 'pointer' : 'not-allowed' }}
            onClick={handleUpload}
            disabled={!file || !selectedAccount || uploading || uploaded}
          >
            {uploaded ? (
              <><Check size={18} /> {scheduleMode === 'now' ? 'Published to TikTok!' : 'Added to Queue!'}</>
            ) : uploading ? (
              <><Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Uploading...</>
            ) : (
              <><UploadIcon size={18} /> {scheduleMode === 'now' ? 'Publish Now' : 'Add to Queue'}</>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        textarea:focus, input:focus { outline: none; border-color: var(--accent) !important; }
        textarea::placeholder, input::placeholder { color: var(--text-muted); }
      `}</style>
    </div>
  );
}

const styles = {
  container: { flex: 1, padding: '32px', overflowY: 'auto', maxWidth: 1000 },
  header: { marginBottom: 20 },
  publishModeRow: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24,
  },
  publishModeBtn: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '16px 18px', border: '1px solid', borderRadius: 'var(--radius-lg)',
    cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
  },
  publishModeIcon: {
    width: 42, height: 42, borderRadius: 10, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  publishModeText: { flex: 1 },
  publishModeTitle: { fontSize: 14, fontWeight: 700, marginBottom: 3 },
  publishModeDesc: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 },
  publishModeRadio: {
    width: 18, height: 18, borderRadius: '50%', border: '2px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  publishModeRadioDot: { width: 8, height: 8, borderRadius: '50%', background: '#fff' },
  title: { fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 },
  subtitle: { fontSize: 14, color: 'var(--text-secondary)' },
  layout: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' },
  leftCol: {},
  rightCol: { display: 'flex', flexDirection: 'column', gap: 18 },
  dropzone: {
    border: '2px dashed', borderRadius: 'var(--radius-lg)',
    padding: '48px 24px', display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'all 0.2s',
    minHeight: 320, justifyContent: 'center',
  },
  dropzoneIcon: {
    width: 72, height: 72, borderRadius: '50%', background: 'var(--accent-light)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8,
  },
  dropzoneText: { fontSize: 17, fontWeight: 600, color: 'var(--text-primary)' },
  dropzoneSub: { fontSize: 13, color: 'var(--text-secondary)' },
  dropzoneTags: { display: 'flex', gap: 8, marginTop: 8 },
  tag: { fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: 'var(--bg-secondary)', color: 'var(--text-muted)', border: '1px solid var(--border)' },
  previewCard: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' },
  previewTop: { position: 'relative' },
  previewVideo: { width: '100%', display: 'block', maxHeight: 280, objectFit: 'cover', background: '#000' },
  removeBtn: {
    position: 'absolute', top: 10, right: 10, width: 30, height: 30, borderRadius: '50%',
    background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  fileInfo: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' },
  fileName: { fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' },
  fileSize: { fontSize: 11, color: 'var(--text-secondary)' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 8, position: 'relative' },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' },
  input: {
    background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
    padding: '10px 14px', color: 'var(--text-primary)', fontSize: 14, width: '100%',
  },
  textarea: {
    background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
    padding: '10px 14px', color: 'var(--text-primary)', fontSize: 14, width: '100%',
    resize: 'vertical', fontFamily: 'inherit',
  },
  charCount: { fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: -4 },
  hint: { fontSize: 11, color: 'var(--text-muted)' },
  selectWrapper: { position: 'relative' },
  selectBtn: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
    background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
    cursor: 'pointer', width: '100%',
  },
  selectBtnText: { flex: 1, fontSize: 14, color: 'var(--text-primary)', textAlign: 'left', fontWeight: 500 },
  accountAvatar: {
    width: 28, height: 28, borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--accent), var(--cyan))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0, overflow: 'hidden',
  },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 10, zIndex: 100, overflow: 'hidden', boxShadow: 'var(--shadow)',
  },
  dropdownItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
    background: 'transparent', border: 'none', cursor: 'pointer', width: '100%',
  },
  scheduleToggle: {
    display: 'flex', background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 10, padding: 4, gap: 4,
  },
  toggleBtn: { flex: 1, padding: '8px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s' },
  dateTimeRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 },
  dateInput: {
    background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
    padding: '10px 14px', color: 'var(--text-primary)', fontSize: 14, colorScheme: 'dark',
  },
  submitBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px',
    background: 'linear-gradient(135deg, var(--accent), var(--pink))',
    color: '#fff', border: 'none', borderRadius: 12,
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
    boxShadow: 'var(--shadow-accent)', marginTop: 4,
  },
};
